/** XeAgents.ai **/
import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";
import type { HealthcareSettings } from "./ClinicPanel";

interface CapabilityOption {
  id: string;
  label: string;
  description: string;
  write: boolean;
  requires: string[];
}

interface ProfileOptions {
  capabilities: CapabilityOption[];
  presets: Array<{ id: string; label: string; description: string; capabilities: string[] }>;
  countries: Array<{ country: string; label: string; languages: Array<{ code: string; name: string }>; askLanguageAtStart: boolean }>;
}

interface Profile {
  preset: string;
  capabilities: Record<string, boolean>;
  greeting: { mentionAI: "on_ask" | "upfront"; listServices: boolean; agentName: string; ringback: boolean };
  languages: { offered: string[]; askAtStart: boolean };
  patience: "normal" | "high";
  sms: { sender: string };
}

export default function CallHandlingPanel({ settings }: { settings: HealthcareSettings | null }) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { data: options } = useQuery<ProfileOptions>({ queryKey: ["/api/healthcare/profile-options"] });
  const [profile, setProfile] = useState<Profile | null>(null);

  useEffect(() => {
    if (settings?.inboundProfileEffective) setProfile(settings.inboundProfileEffective as Profile);
  }, [settings]);

  const save = useMutation({
    mutationFn: async (p: Profile) => {
      const { capabilities, greeting, languages, patience, sms } = p;
      return (await apiRequest("PUT", "/api/healthcare/settings", { inboundProfile: { capabilities, greeting, languages, patience, sms } })).json();
    },
    onSuccess: () => {
      toast({ title: "Call handling saved", description: "The next inbound call uses these settings." });
      queryClient.invalidateQueries({ queryKey: ["/api/healthcare/settings"] });
    },
    onError: (e: any) => toast({ title: "Could not save", description: e.message, variant: "destructive" }),
  });

  if (!options || !profile) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Call handling</CardTitle>
          <CardDescription>{settings ? "Loading…" : "Save your clinic details first."}</CardDescription>
        </CardHeader>
      </Card>
    );
  }

  const country = options.countries.find((c) => c.country === settings?.country);
  const countryLanguages = country?.languages ?? [];

  const setCapability = (id: string, on: boolean) =>
    setProfile((p) => {
      if (!p) return p;
      const capabilities = { ...p.capabilities, [id]: on };
      // Switching one on switches on what it needs; switching booking off switches off what depends on it.
      if (on) for (const need of options.capabilities.find((c) => c.id === id)?.requires ?? []) capabilities[need] = true;
      else for (const c of options.capabilities) if (c.requires.includes(id)) capabilities[c.id] = false;
      return { ...p, preset: "custom", capabilities };
    });

  const applyPreset = (id: string) => {
    const preset = options.presets.find((x) => x.id === id);
    if (!preset) return;
    setProfile((p) => (p ? { ...p, preset: id, capabilities: Object.fromEntries(options.capabilities.map((c) => [c.id, preset.capabilities.includes(c.id)])) } : p));
  };

  const toggleLanguage = (code: string, on: boolean) =>
    setProfile((p) => {
      if (!p) return p;
      const offered = on ? [...new Set([...p.languages.offered, code])] : p.languages.offered.filter((c) => c !== code);
      return { ...p, languages: { offered, askAtStart: offered.length > 1 && p.languages.askAtStart } };
    });

  const noLanguage = profile.languages.offered.length === 0;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Call handling</CardTitle>
        <CardDescription>
          What the receptionist may do on inbound calls. Anything switched off is removed from the call completely, so it cannot be done by mistake. Start with information and messages, then add booking once your system is connected and tested.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="space-y-2">
          <p className="text-sm font-medium">Start from</p>
          <div className="flex flex-wrap gap-2">
            {options.presets.map((p) => (
              <Button key={p.id} size="sm" variant={profile.preset === p.id ? "default" : "outline"} title={p.description} onClick={() => applyPreset(p.id)} data-testid={`preset-${p.id}`}>
                {p.label}
              </Button>
            ))}
            {profile.preset === "custom" && <Badge variant="secondary">Custom</Badge>}
          </div>
        </div>

        <div className="space-y-2">
          {options.capabilities.map((c) => (
            <label key={c.id} className="flex items-start gap-3 rounded-md border p-3" data-testid={`capability-${c.id}`}>
              <Switch checked={!!profile.capabilities[c.id]} onCheckedChange={(on) => setCapability(c.id, on)} />
              <span className="space-y-0.5">
                <span className="flex items-center gap-2 text-sm font-medium">
                  {c.label}
                  {c.write && <Badge variant="outline">Changes records</Badge>}
                </span>
                <span className="block text-xs text-muted-foreground">{c.description}</span>
              </span>
            </label>
          ))}
        </div>

        <div className="space-y-3">
          <p className="text-sm font-medium">Greeting</p>
          <div className="flex items-center gap-3 text-sm">
            <span className="w-56">Name she gives callers</span>
            <Input
              className="w-64"
              value={profile.greeting.agentName}
              maxLength={30}
              placeholder="Leave empty to use her voice's name"
              aria-label="Receptionist name"
              onChange={(e) => setProfile({ ...profile, greeting: { ...profile.greeting, agentName: e.target.value } })}
            />
          </div>
          <label className="flex items-center gap-3 text-sm">
            <Switch checked={profile.greeting.ringback} onCheckedChange={(ringback) => setProfile({ ...profile, greeting: { ...profile.greeting, ringback } })} />
            Play a ringing tone before she answers
          </label>
          <label className="flex items-center gap-3 text-sm">
            <Switch checked={profile.greeting.listServices} onCheckedChange={(listServices) => setProfile({ ...profile, greeting: { ...profile.greeting, listServices } })} />
            List what she can help with in the greeting (off: she simply invites any request)
          </label>
          <div className="flex items-center gap-3 text-sm">
            <span className="w-56">Say it is an AI assistant</span>
            <Select value={profile.greeting.mentionAI} onValueChange={(mentionAI) => setProfile({ ...profile, greeting: { ...profile.greeting, mentionAI: mentionAI as "on_ask" | "upfront" } })}>
              <SelectTrigger className="w-64" aria-label="When to say it is an AI">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="on_ask">Only when the caller asks</SelectItem>
                <SelectItem value="upfront">In the greeting</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="space-y-3">
          <p className="text-sm font-medium">Languages</p>
          {countryLanguages.length === 0 ? (
            <p className="text-xs text-muted-foreground">Choose the clinic's country first.</p>
          ) : (
            <div className="flex flex-wrap gap-4">
              {countryLanguages.map((l) => (
                <label key={l.code} className="flex items-center gap-2 text-sm">
                  <Switch checked={profile.languages.offered.includes(l.code)} onCheckedChange={(on) => toggleLanguage(l.code, on)} /> {l.name} ({l.code})
                </label>
              ))}
            </div>
          )}
          <label className="flex items-center gap-3 text-sm">
            <Switch
              checked={profile.languages.askAtStart}
              disabled={profile.languages.offered.length < 2}
              onCheckedChange={(askAtStart) => setProfile({ ...profile, languages: { ...profile.languages, askAtStart } })}
            />
            Ask the caller which language they prefer
          </label>
          {noLanguage && <p className="text-xs text-destructive">Offer at least one language.</p>}
        </div>

        <div className="flex items-center gap-3 text-sm">
          <span className="w-56">Pause before answering</span>
          <Select value={profile.patience} onValueChange={(patience) => setProfile({ ...profile, patience: patience as "normal" | "high" })}>
            <SelectTrigger className="w-64" aria-label="Pause before answering">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="high">Patient: waits for callers who pause</SelectItem>
              <SelectItem value="normal">Normal: answers sooner</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <p className="text-sm font-medium">Confirmation texts</p>
          <div className="flex items-center gap-3 text-sm">
            <span className="w-56">Send them from</span>
            <Input
              className="w-64"
              value={profile.sms?.sender ?? ""}
              maxLength={16}
              placeholder="Empty: the number she answered on"
              aria-label="Confirmation text sender"
              onChange={(e) => setProfile({ ...profile, sms: { sender: e.target.value } })}
            />
          </div>
          <p className="text-xs text-muted-foreground">
            A number in international format (+61290001234) or a sender name of 3 to 11 letters. It must be able to send texts: many numbers can only take calls, and some countries (India) only allow registered senders. Switch the texts on under Clinic details.
          </p>
        </div>

        <Button onClick={() => save.mutate(profile)} disabled={save.isPending || noLanguage} data-testid="button-save-call-handling">
          {save.isPending ? "Saving…" : "Save call handling"}
        </Button>
      </CardContent>
    </Card>
  );
}
